import React from 'react';
import { AlertCircle, Lock, Crown } from 'lucide-react';

// Feature access mapping - which tiers can access which features
const FEATURE_TIERS = {
  // Premium features (Premium + Pro)
  rate_refresh: ['premium', 'pro'],
  rate_history: ['premium', 'pro'],
  rate_forecasts: ['premium', 'pro'],
  weekly_digest: ['premium', 'pro'],
  sms_notifications: ['premium', 'pro'],
  unlimited_offers: ['premium', 'pro'],
  
  // Pro-only features
  heloc_calculator: ['pro'],
  unlimited_scenarios: ['pro'],
  export_reports: ['pro'],
  priority_support: ['pro'],
  advanced_analytics: ['pro'],
  
  // Free features (available to all)
  basic_tracking: ['free', 'premium', 'pro'],
  renewal_timeline: ['free', 'premium', 'pro']
};

// Feature descriptions for upgrade prompts
const FEATURE_INFO = {
  rate_refresh: {
    name: 'Live Rate Updates',
    description: 'Get real-time mortgage rate updates from major Canadian lenders.',
    requiredTier: 'Premium'
  },
  rate_history: {
    name: 'Rate History',
    description: 'View historical rate trends to make informed timing decisions.',
    requiredTier: 'Premium'
  },
  rate_forecasts: {
    name: 'Rate Forecasts',
    description: 'Access Bank of Canada rate predictions and market analysis.',
    requiredTier: 'Premium'
  },
  weekly_digest: {
    name: 'Weekly Digest',
    description: 'Receive personalized weekly email reports with rate trends and recommendations.',
    requiredTier: 'Premium'
  },
  sms_notifications: {
    name: 'SMS Notifications',
    description: 'Get instant SMS alerts for rate changes and renewal reminders.',
    requiredTier: 'Premium'
  },
  unlimited_offers: {
    name: 'Unlimited Offers',
    description: 'Track unlimited mortgage offers from different lenders.',
    requiredTier: 'Premium'
  },
  heloc_calculator: {
    name: 'HELOC Calculator',
    description: 'Calculate your Home Equity Line of Credit eligibility and options.',
    requiredTier: 'Pro'
  },
  unlimited_scenarios: {
    name: 'Unlimited Scenarios',
    description: 'Create unlimited prepayment and refinancing scenarios.',
    requiredTier: 'Pro'
  },
  export_reports: {
    name: 'Export Reports',
    description: 'Download professional PDF reports of your mortgage analysis.',
    requiredTier: 'Pro'
  },
  priority_support: {
    name: 'Priority Support',
    description: 'Get faster response times and dedicated support.',
    requiredTier: 'Pro'
  },
  advanced_analytics: {
    name: 'Advanced Analytics',
    description: 'Deep dive into mortgage market trends and predictions.',
    requiredTier: 'Pro'
  }
};

const PaywallGuard = ({ children, feature, user, fallbackMessage, compact = false }) => {
  if (!user) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} bg-card border border-border rounded-lg`}>
        <div className="flex items-center gap-2 text-muted-foreground">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">Please log in to access this feature</span>
        </div>
      </div>
    );
  }
  
  const requiredTiers = FEATURE_TIERS[feature] || [];
  const userTier = user.subscription_tier || 'free';
  
  // Check if user has access
  if (requiredTiers.length === 0 || requiredTiers.includes(userTier)) {
    return children;
  }
  
  // Get feature info for upgrade prompt
  const featureInfo = FEATURE_INFO[feature] || {
    name: 'Premium Feature',
    description: fallbackMessage || 'This feature requires a premium subscription.',
    requiredTier: 'Premium'
  };
  
  // Compact version for inline elements
  if (compact) {
    return (
      <div className="p-3 bg-card border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-primary" />
          <span className="text-sm text-muted-foreground">
            {featureInfo.requiredTier} feature
          </span>
          <a
            href="/dashboard?tab=settings"
            className="text-xs text-primary hover:underline ml-auto"
          >
            Upgrade
          </a>
        </div>
      </div>
    );
  }
  
  // Full paywall message
  return (
    <div className="p-6 bg-card border border-border rounded-xl">
      <div className="text-center max-w-md mx-auto">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4">
          <Crown className="h-6 w-6 text-primary" />
        </div>
        
        <h3 className="text-lg font-semibold mb-2">{featureInfo.name}</h3>
        
        <p className="text-muted-foreground text-sm mb-4">
          {featureInfo.description}
        </p>
        
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-xs text-muted-foreground">Requires</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            featureInfo.requiredTier === 'Pro' 
              ? 'bg-purple-500/20 text-purple-400' 
              : 'bg-primary/20 text-primary'
          }`}>
            {featureInfo.requiredTier}
          </span>
        </div>
        
        <a
          href="/dashboard?tab=settings"
          className="inline-flex items-center justify-center px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
        >
          View Plans
        </a>
      </div>
    </div>
  );
};

// Helper hook to check feature access
export const useFeatureAccess = (user, feature) => {
  const requiredTiers = FEATURE_TIERS[feature] || [];
  const userTier = user?.subscription_tier || 'free';
  return requiredTiers.length === 0 || requiredTiers.includes(userTier);
};

// Helper to get all features for a tier
export const getTierFeatures = (tier) => {
  const features = {};
  for (const [feature, tiers] of Object.entries(FEATURE_TIERS)) {
    features[feature] = tiers.includes(tier);
  }
  return features;
};

export default PaywallGuard;